import { useFavoriteTab } from '../../hooks/useFavoriteTab';
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import quoteMigrationService, {
  type ChargeMappingRow, type CatalogRow, type MigrationStats,
  type ActivePair, type QuoteDrilldown, type MigrateResult,
  type VerificationStats, type MigratedUnverifiedRow, type RateZoneMappingRow,
  type WorklistQuotes, type MigrationHistoryRow,
} from '../../services/quoteMigration.service';
import { TanTable } from '../../components/TanTable';
import { Tooltip } from '../../components/ui/Tooltip';
import { isMissingCustomerTariff, MISSING_TARIFF_LABEL } from '../../utils/fscTariff';

const errMsg = (e: unknown): string => {
  const ax = e as { response?: { data?: { detail?: string } }; message?: string };
  return ax?.response?.data?.detail || ax?.message || String(e);
};

const MATCH_STYLES: Record<string, string> = {
  matched: 'text-green-700 dark:text-green-400',
  override: 'text-blue-700 dark:text-blue-400',
  ambiguous: 'text-amber-700 dark:text-amber-400',
  unmatched: 'text-red-700 dark:text-red-400',
  skip: 'text-slate-400',
};

/** The "Used" count cell — hovering shows the v2 quote numbers behind the count.
 *  `quotes` is the list for this row (undefined until the shared fetch resolves);
 *  `ready` is true once that fetch completed (so absent-key = "no quotes", not loading). */
const UsedCell: React.FC<{
  count: number;
  quotes: number[] | undefined;
  ready: boolean;
  error: boolean;
  kind: 'charge' | 'zone';
}> = ({ count, quotes, ready, error, kind }) => {
  const MAX = 60;
  let content: React.ReactNode;
  if (error) {
    content = <div className="text-[0.6875rem]">Quote lookup unavailable</div>;
  } else if (!ready) {
    content = <div className="text-[0.6875rem] text-slate-300">Loading quotes…</div>;
  } else if (!quotes || quotes.length === 0) {
    content = <div className="text-[0.6875rem]">No v2 quotes found</div>;
  } else {
    const shown = quotes.slice(0, MAX);
    content = (
      <div className="text-[0.6875rem] max-w-[260px]">
        <div className="font-semibold mb-1">v2 quotes using this {kind} ({quotes.length})</div>
        <div className="leading-relaxed break-words">
          {shown.join(', ')}{quotes.length > shown.length ? `, +${quotes.length - shown.length} more` : ''}
        </div>
      </div>
    );
  }
  return (
    <Tooltip content={content} placement="auto">
      <span className="cursor-help underline decoration-dotted decoration-slate-400 underline-offset-2 tabular-nums">
        {count}
      </span>
    </Tooltip>
  );
};

/** Shared eager fetch of the "Used" → v2-quote-number maps (cached across both tabs). */
const useWorklistQuotes = () => {
  const [wq, setWq] = useState<WorklistQuotes | null>(null);
  const [wqError, setWqError] = useState(false);
  useEffect(() => {
    let alive = true;
    quoteMigrationService.worklistQuotes()
      .then((d) => { if (alive) setWq(d); })
      .catch(() => { if (alive) setWqError(true); });
    return () => { alive = false; };
  }, []);
  return { wq, wqReady: wq !== null, wqError };
};

const FieldSection: React.FC<{ title: string; entries: [string, React.ReactNode][] }> = ({ title, entries }) => (
  <div>
    <div className="text-[0.75rem] font-semibold text-slate-600 dark:text-slate-300 mb-1">{title}</div>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-[0.78125rem]">
      {entries.map(([k, v]) => (
        <div key={k} className="rounded border border-slate-200 dark:border-slate-700 px-2 py-1">
          <span className="text-slate-500">{k}: </span>
          <span className="text-slate-800 dark:text-slate-200">{v === null || v === undefined || v === '' ? '—' : v}</span>
        </div>
      ))}
    </div>
  </div>
);

/** "Quotes (preview)" tab — pick an active (v2quotenumber, v2lane) pair and see
 *  the proposed v1 quote rows (read live from v2). */
const QuotesView: React.FC = () => {
  const [pairs, setPairs] = useState<ActivePair[]>([]);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState<ActivePair | null>(null);
  const [detail, setDetail] = useState<QuoteDrilldown | null>(null);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [bulk, setBulk] = useState<MigrateResult | null>(null);
  // The v1 quote that the last migrate produced (created or already-existing), so
  // we can offer an "Open in 360" link to go verify/edit it.
  const [openQuote, setOpenQuote] = useState<{ quotenumber: number; revisionnumber: number } | null>(null);
  // "Not Migrating" exclusion: toggle to reveal excluded lanes, an inline reason
  // input in the detail pane, and the last-excluded pair for a one-click Undo.
  const [showExcluded, setShowExcluded] = useState(false);
  const [excludeOpen, setExcludeOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [undoPair, setUndoPair] = useState<ActivePair | null>(null);

  useEffect(() => {
    quoteMigrationService.listQuotes()
      .then(setPairs).catch((e) => setError(String(e))).finally(() => setLoading(false));
  }, []);

  const migrateOne = async () => {
    if (!sel) return;
    setBusy('one'); setMsg(null); setError(null); setOpenQuote(null);
    try {
      const r = await quoteMigrationService.migrateOne(sel.v2quotenumber, sel.v2lane);
      const id = `${sel.v2quotenumber}-${sel.v2lane}`;
      if (r.status === 'already_migrated') {
        const ex = r.skipped?.[0];
        setMsg(`${id} was already migrated.`);
        if (ex) setOpenQuote({ quotenumber: ex.quotenumber, revisionnumber: ex.revisionnumber });
      } else {
        const made = r.quotes[0];
        setMsg(`Migrated ${id} → v1 quote ${made?.quotenumber} (${made?.charges} charge lines).`);
        if (made) setOpenQuote({ quotenumber: made.quotenumber, revisionnumber: 1 });
      }
      setDetail(await quoteMigrationService.quoteDrilldown(sel.v2quotenumber, sel.v2lane));
      // Refetch so the just-migrated pair drops off the to-migrate list.
      setPairs(await quoteMigrationService.listQuotes());
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(null); }
  };

  const runBulk = async (execute: boolean) => {
    if (execute && !window.confirm('Migrate ALL ready (resolved, not-yet-migrated) quotes into v1? This writes data.')) return;
    setBusy(execute ? 'bulk-run' : 'bulk-prev'); setMsg(null); setError(null); setBulk(null);
    try {
      setBulk(await quoteMigrationService.runMigration(execute));
    }
    catch (e) { setError(errMsg(e)); }
    finally { setBusy(null); }
  };

  const pick = async (p: ActivePair) => {
    setSel(p); setDetail(null); setDetailLoading(true); setError(null);
    setExcludeOpen(false); setReason('');
    try { setDetail(await quoteMigrationService.quoteDrilldown(p.v2quotenumber, p.v2lane)); }
    catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    finally { setDetailLoading(false); }
  };

  const excludeSel = async () => {
    if (!sel) return;
    setBusy('exclude'); setMsg(null); setError(null); setOpenQuote(null); setUndoPair(null);
    try {
      await quoteMigrationService.excludeQuote(sel.v2quotenumber, sel.v2lane, reason.trim() || undefined);
      setMsg(`Marked ${sel.v2quotenumber}-${sel.v2lane} Not Migrating.`);
      setUndoPair(sel);
      setExcludeOpen(false); setReason('');
      setPairs(await quoteMigrationService.listQuotes());
      // With the toggle off, the excluded lane leaves the list — clear the pane.
      if (!showExcluded) { setSel(null); setDetail(null); }
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(null); }
  };

  const restoreSel = async (p?: ActivePair) => {
    const target = p || sel;
    if (!target) return;
    setBusy('restore'); setError(null); setUndoPair(null);
    if (!p) setMsg(null);   // keep the Undo message when restoring via Undo
    try {
      await quoteMigrationService.restoreQuote(target.v2quotenumber, target.v2lane);
      setMsg(`Restored ${target.v2quotenumber}-${target.v2lane} to the worklist.`);
      setPairs(await quoteMigrationService.listQuotes());
    } catch (e) { setError(errMsg(e)); }
    finally { setBusy(null); }
  };

  // Bucket the active set: tool-migrated (drives "Migrated") → "Not Migrating"
  // (excluded) → "Already in V1" (a RECENT v1 quote exists — created on/after the
  // migration cutoff — but the tool didn't make it) → "To migrate" (no recent v1
  // quote; includes lanes whose only v1 match is a pre-cutoff legacy cross-reference).
  // These four sum to the active set. "Already in V1" stays off the actionable list
  // (the runner skips recent matches); legacy-only lanes are genuinely to-migrate.
  const migratedCount = useMemo(() => pairs.filter((p) => p.migrated).length, [pairs]);
  const notMigrated = useMemo(() => pairs.filter((p) => !p.migrated), [pairs]);
  const excludedPending = useMemo(() => notMigrated.filter((p) => p.excluded), [notMigrated]);
  const nonExcluded = useMemo(() => notMigrated.filter((p) => !p.excluded), [notMigrated]);
  const alreadyInV1 = useMemo(() => nonExcluded.filter((p) => p.inV1), [nonExcluded]);
  // "To migrate" = no RECENT v1 match. This now includes legacy-in-v1 lanes (a matched
  // v1 quote that predates the migration cutoff): those stray cross-references are
  // ignored by the backend (inV1=false), so the lane is genuinely still to migrate.
  const toMigrate = useMemo(() => nonExcluded.filter((p) => !p.inV1), [nonExcluded]);
  // The actionable list is genuinely-to-migrate lanes; the toggle reveals excluded
  // ones. "Already in V1" lanes are done (outside the tool) and stay off the list.
  const visible = useMemo(
    () => (showExcluded ? [...toMigrate, ...excludedPending] : toMigrate),
    [toMigrate, excludedPending, showExcluded],
  );
  const filtered = useMemo(
    () => (q ? visible.filter((p) => `${p.v2quotenumber}-${p.v2lane}`.toLowerCase().includes(q.toLowerCase())) : visible),
    [visible, q],
  );
  // Is the selected lane currently excluded? Read from the (refreshed) pairs, not
  // stale `sel`, so the detail-pane action flips right after exclude/restore.
  const selExcluded = useMemo(() => {
    if (!sel) return false;
    const cur = pairs.find((p) => p.v2quotenumber === sel.v2quotenumber && p.v2lane === sel.v2lane);
    return !!cur?.excluded;
  }, [pairs, sel]);

  if (loading) return <div className="text-slate-500">Loading active quotes…</div>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => runBulk(false)} disabled={!!busy}
          className="px-3 py-1.5 text-[0.78125rem] font-medium border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50">
          {busy === 'bulk-prev' ? 'Previewing…' : 'Preview migration'}
        </button>
        {/* "Migrate all ready" removed — quotes are migrated one at a time (per
            quote, via "Migrate this quote") so each can be reviewed before pull-in. */}
        {bulk && (
          <span className="text-[0.78125rem] text-slate-600 dark:text-slate-300">
            {bulk.executed ? 'Migrated' : 'Would migrate'} <b>{bulk.created}</b> quote(s),
            {' '}{bulk.total_charges} charge lines · skipped {bulk.skipped_existing} already-migrated
            {bulk.excluded ? ` · ${bulk.excluded} not migrating` : ''}
            {bulk.missing.length ? ` · ${bulk.missing.length} missing v2 lane` : ''}
            {bulk.flagged.length ? ` · ${bulk.flagged.length} flagged` : ''}.
          </span>
        )}
      </div>
      {/* Migration progress over the active set (these sum: active = migrated +
          already-in-v1 + to-migrate + not-migrating). "Migrated" is TOOL-migrated only
          (v2_quote_verification); "Already in V1" is lanes with a RECENT v1 quote
          (on/after the 2025-06-01 match cutoff) the tool didn't make. Lanes whose only
          v1 match predates the cutoff count as "To migrate". Verification progress
          lives on the "Migrated · Unverified" tab. */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {[
          ['Active set', pairs.length, ''],
          ['Migrated', migratedCount, 'text-green-700 dark:text-green-400'],
          ['Already in V1', alreadyInV1.length, alreadyInV1.length ? 'text-slate-500 dark:text-slate-400' : ''],
          ['To migrate', toMigrate.length, toMigrate.length === 0 ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'],
          ['Not migrating', excludedPending.length, excludedPending.length ? 'text-slate-500 dark:text-slate-400' : ''],
        ].map(([label, val, cls]) => (
          <div key={label as string} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
            <div className="text-[0.6875rem] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
            <div className={`text-2xl font-semibold ${cls as string}`}>{val as number}</div>
          </div>
        ))}
      </div>
      {error && <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">{error}</div>}
      {msg && (
        <div className="rounded-lg border border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-900/30 p-3 text-sm text-green-800 dark:text-green-300 flex flex-wrap items-center gap-3">
          <span>{msg}</span>
          {openQuote && (
            <Link to={`/order-management/quotes/${openQuote.quotenumber}-${openQuote.revisionnumber}`}
              className="font-semibold underline hover:no-underline whitespace-nowrap">
              Open quote {openQuote.quotenumber} in 360 →
            </Link>
          )}
          {undoPair && (
            <button type="button" onClick={() => restoreSel(undoPair)} disabled={!!busy}
              className="font-semibold underline hover:no-underline whitespace-nowrap disabled:opacity-50">
              Undo
            </button>
          )}
        </div>
      )}

    <div className="grid grid-cols-1 md:grid-cols-[260px_1fr] gap-4">
      <div className="rounded-lg border border-slate-200 dark:border-slate-700">
        <div className="p-2 border-b border-slate-200 dark:border-slate-700 space-y-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${visible.length}…`}
            className="w-full px-2 py-1 text-[0.78125rem] border border-slate-300 dark:border-slate-600 rounded dark:bg-slate-700" />
          {excludedPending.length > 0 && (
            <label className="flex items-center gap-1.5 text-[0.71875rem] text-slate-600 dark:text-slate-300 cursor-pointer select-none">
              <input type="checkbox" checked={showExcluded} onChange={(e) => setShowExcluded(e.target.checked)} />
              Show Not Migrating ({excludedPending.length})
            </label>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {filtered.map((p) => {
            const id = `${p.v2quotenumber}-${p.v2lane}`;
            const active = sel && sel.v2quotenumber === p.v2quotenumber && sel.v2lane === p.v2lane;
            return (
              <div key={id}
                className={`flex items-center border-b border-slate-100 dark:border-slate-800 ${active ? 'bg-blue-50 dark:bg-blue-900/30' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}>
                <button type="button" onClick={() => pick(p)}
                  title={p.excluded && p.reason ? `Not Migrating: ${p.reason}` : undefined}
                  className={`flex-1 min-w-0 text-left px-3 py-1.5 text-[0.78125rem] ${active ? 'font-medium' : ''} ${p.excluded ? 'text-slate-400 line-through' : ''}`}>
                  {id}
                  {p.cancelled && (
                    <span className="ml-2 no-underline inline-block text-[0.625rem] uppercase tracking-wide text-rose-600 dark:text-rose-400">
                      v2 Canceled
                    </span>
                  )}
                  {p.excluded && (
                    <span className="ml-2 no-underline inline-block text-[0.625rem] uppercase tracking-wide text-amber-600 dark:text-amber-400">
                      Not Migrating
                    </span>
                  )}
                </button>
                {p.excluded && (
                  <button type="button" onClick={() => restoreSel(p)} disabled={!!busy}
                    className="px-2 py-1 mr-1 text-[0.6875rem] text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50 whitespace-nowrap">
                    Restore
                  </button>
                )}
              </div>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-3 py-8 text-center text-slate-400 text-[0.78125rem]">
              {q ? 'No matches.' : 'Nothing left to migrate. 🎉'}
            </div>
          )}
        </div>
      </div>

      <div>
        {!sel && <div className="text-slate-400 text-sm">Select a quote to preview its proposed v1 rows.</div>}
        {detailLoading && <div className="text-slate-500 text-sm">Loading {sel?.v2quotenumber}-{sel?.v2lane}…</div>}
        {detail && (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">
                v2 {detail.v2quotenumber}-{detail.v2lane} → proposed v1 quote
                {selExcluded && <span className="ml-2 text-[0.75rem] font-medium uppercase tracking-wide text-amber-600 dark:text-amber-400">· Not Migrating</span>}
              </h2>
              <div className="flex items-center gap-2">
                {selExcluded ? (
                  <button type="button" onClick={() => restoreSel()} disabled={!!busy}
                    className="px-3 py-1.5 text-[0.78125rem] font-medium border border-blue-300 text-blue-700 dark:border-blue-700 dark:text-blue-300 rounded hover:bg-blue-50 dark:hover:bg-blue-900/30 disabled:opacity-50 whitespace-nowrap">
                    {busy === 'restore' ? 'Restoring…' : 'Restore to worklist'}
                  </button>
                ) : (
                  <>
                    <button type="button" onClick={migrateOne} disabled={!!busy}
                      className="px-3 py-1.5 text-[0.78125rem] font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap">
                      {busy === 'one' ? 'Migrating…' : 'Migrate this quote'}
                    </button>
                    <button type="button" onClick={() => setExcludeOpen((v) => !v)} disabled={!!busy}
                      className="px-3 py-1.5 text-[0.78125rem] font-medium border border-slate-300 dark:border-slate-600 rounded hover:bg-slate-50 dark:hover:bg-slate-700 disabled:opacity-50 whitespace-nowrap">
                      Not Migrating
                    </button>
                  </>
                )}
              </div>
            </div>
            {excludeOpen && !selExcluded && (
              <div className="flex flex-wrap items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/20 p-2">
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)"
                  className="flex-1 min-w-[160px] px-2 py-1 text-[0.78125rem] border border-slate-300 dark:border-slate-600 rounded dark:bg-slate-700" />
                <button type="button" onClick={excludeSel} disabled={!!busy}
                  className="px-3 py-1.5 text-[0.78125rem] font-medium text-white bg-amber-600 rounded hover:bg-amber-700 disabled:opacity-50 whitespace-nowrap">
                  {busy === 'exclude' ? 'Saving…' : 'Confirm Not Migrating'}
                </button>
                <button type="button" onClick={() => { setExcludeOpen(false); setReason(''); }}
                  className="px-2 py-1 text-[0.78125rem] text-slate-500 hover:underline">Cancel</button>
              </div>
            )}
            <div className="text-[0.8125rem] text-slate-600 dark:text-slate-300">
              <b>v2 Expiration Date:</b> {detail.v2ExpirationDate ? String(detail.v2ExpirationDate).slice(0, 10) : '—'}
            </div>
            {detail.flags.length > 0 && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-900/30 p-2 text-[0.75rem] text-amber-800 dark:text-amber-300">
                <b>Field flags:</b> {detail.flags.join('; ')}
              </div>
            )}
            <FieldSection title="Quote header (masterquote)" entries={[
              ['Bill to', [detail.master.billto, detail.billtoName].filter(Boolean).join(' — ')],
              ['Customer code', [detail.master.customercode, detail.customerName].filter(Boolean).join(' — ')],
              ['Sales CSR', detail.master.salescsr], ['CSR', detail.master.csr],
              ['SF Opportunity', detail.master.salesforceopportunityid],
              ['Fuel calc', detail.master.fuelcalctype], ['Rate applies', detail.master.rateapplies],
              ['From v2', `${detail.master.v2quotenumber}-${detail.master.v2lane}`],
            ]} />
            <FieldSection title="Revision (quoterevisions)" entries={[
              ['Status', detail.revision.status], ['Quoted by', detail.revision.quotedby],
              ['Sold by', detail.revision.soldby],
              ['Secure date (backfill)', detail.revision.securedate], ['Est. margin', detail.revision.estimatedmargin],
              ['Volume', detail.revision.volume],
              ['Effective', detail.revision.effectivedate], ['End', detail.revision.enddate],
            ]} />
            <FieldSection title="Direct (directquote)" entries={[
              ['Move type', detail.direct.movetype], ['Rate zone', detail.direct.ratezone],
              ['Delivery zone', detail.direct.deliveryzone],
              ['Deliver to', [detail.direct.delivertocity, detail.direct.delivertostate, detail.direct.delivertozip].filter(Boolean).join(' ')],
              ['Dray $', detail.direct.drayrate], ['Dray w/charges', detail.direct.draywithcharges],
              ['FSC', detail.direct.fsctype], ['FSC rate', detail.direct.fscrate],
              ['FSC tariff', isMissingCustomerTariff(detail.direct.fsctype, detail.direct.fsctariff)
                ? <span className="font-semibold text-red-600 dark:text-red-400">{MISSING_TARIFF_LABEL}</span>
                : detail.direct.fsctariff],
              ['Chassis', `${detail.direct.chassisfeetype ?? ''} ${detail.direct.chassisfeedays ?? ''}`.trim()],
              ['LW chassis', `${detail.direct.lightweightchassisfeetype ?? ''} ${detail.direct.lightweightchassisfeedays ?? ''}`.trim()],
              ['Wait free', detail.direct.waitingtimefree], ['Yard free', detail.direct.yardstoragefree],
              ['Commodity', detail.direct.commodity],
              ['Size/Type', [detail.direct.size, detail.direct.equiptype].filter(Boolean).join(' / ')],
              ['Cargo $', detail.direct.cargovalue],
            ]} />
            <div>
              <div className="text-[0.75rem] font-semibold text-slate-600 dark:text-slate-300 mb-1">
                Additional charge line items ({detail.charges.length})
              </div>
              <table className="w-full text-[0.78125rem]">
                <thead className="text-left text-slate-500"><tr>
                  <th className="py-1">v2 charge</th><th>Rate type</th><th className="text-right">Rate</th>
                  <th>Free</th><th>2-tier</th><th>v1 recno</th><th>Match</th>
                </tr></thead>
                <tbody>
                  {detail.charges.map((c, i) => (
                    <tr key={i} className="border-t border-slate-100 dark:border-slate-800">
                      <td className="py-1">{c.v2_charge_code}</td>
                      <td>{c.addlchargeratetype}</td>
                      <td className="text-right tabular-nums">{c.addlchargerate.toFixed(2)}</td>
                      <td>{c.freetimeunit ? `${c.freetime} ${c.freetimeunit}` : '—'}</td>
                      <td>{c.two_tier ? '✓' : ''}</td>
                      <td>{c.additionalcharges_recno ?? '—'}</td>
                      <td className={MATCH_STYLES[c.match_status]}>{c.match_status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};

/** "Migrated · Unverified" tab — the worklist of v1 quotes that came from v2 but
 *  haven't been verified in 360 yet. Each links straight to the quote so the CSR
 *  can review + click Verify. */
const MigratedUnverifiedView: React.FC = () => {
  const [rows, setRows] = useState<MigratedUnverifiedRow[]>([]);
  const [vstats, setVstats] = useState<VerificationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    Promise.all([quoteMigrationService.migratedUnverified(), quoteMigrationService.verificationStats()])
      .then(([r, s]) => { setRows(r); setVstats(s); })
      .catch((e) => setError(errMsg(e))).finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    if (!q) return rows;
    const t = q.toLowerCase();
    return rows.filter((r) => `${r.quotenumber} ${r.v2quotenumber}-${r.v2lane ?? ''}`.toLowerCase().includes(t));
  }, [rows, q]);

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-3">
      {error && <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">{error}</div>}
      {/* Verification progress across all tool-migrated quotes (migrated = verified + unverified). */}
      {vstats && (
        <div className="grid grid-cols-3 gap-3">
          {[
            ['Migrated', vstats.migrated, ''],
            ['Verified', vstats.verified, 'text-green-700 dark:text-green-400'],
            ['Unverified', vstats.remaining, vstats.remaining === 0 ? 'text-green-700 dark:text-green-400' : 'text-amber-700 dark:text-amber-400'],
          ].map(([label, val, cls]) => (
            <div key={label as string} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
              <div className="text-[0.6875rem] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
              <div className={`text-2xl font-semibold ${cls as string}`}>{val as number}</div>
            </div>
          ))}
        </div>
      )}
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={`Search ${rows.length}… (v1 quote # or v2 #-lane)`}
        className="w-full max-w-md px-2 py-1 text-[0.78125rem] border border-slate-300 dark:border-slate-600 rounded dark:bg-slate-700" />
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 max-h-[60vh] overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
        {filtered.map((r) => (
          <div key={r.quotenumber} className="flex items-center justify-between gap-3 px-3 py-2 text-[0.78125rem]">
            <span className="text-slate-800 dark:text-slate-200">
              v1 quote <b>{r.quotenumber}</b>
              <span className="text-slate-400 dark:text-slate-500"> · from v2 {r.v2quotenumber}-{r.v2lane ?? ''}</span>
            </span>
            <Link to={`/order-management/quotes/${r.quotenumber}-${r.revisionnumber}`}
              className="font-semibold text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap">
              Open in 360 →
            </Link>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="px-3 py-8 text-center text-slate-400 text-[0.78125rem]">
            {q ? 'No matches.' : 'Nothing to verify — every migrated quote is verified. 🎉'}
          </div>
        )}
      </div>
    </div>
  );
};

const fmtDateTime = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  }) : '';

/** "Migration History" tab — the full audit list of every quote this tool migrated
 *  (newest first): the v1/v2 quote, when it was migrated, and its verify status.
 *  Read-only TanTable → sort / column-filter / Excel export via the built-in toolbar.
 *  Data is INNER-JOINed to a live masterquote, so every row links straight to 360. */
const MigrationHistoryView: React.FC = () => {
  const [rows, setRows] = useState<MigrationHistoryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    quoteMigrationService.migrationHistory()
      .then(setRows).catch((e) => setError(errMsg(e))).finally(() => setLoading(false));
  }, []);

  const columns = useMemo<ColumnDef<MigrationHistoryRow>[]>(() => [
    {
      accessorKey: 'quotenumber',
      header: 'v1 Quote #',
      cell: (info) => {
        const r = info.row.original;
        return (
          <Link to={`/order-management/quotes/${r.quotenumber}-${r.revisionnumber}`}
            className="font-semibold text-blue-600 dark:text-blue-400 hover:underline">
            {r.quotenumber}
          </Link>
        );
      },
      size: 100,
    },
    { accessorKey: 'v2quotenumber', header: 'v2 Quote', size: 90 },
    { accessorKey: 'v2lane', header: 'Lane', cell: (i) => (i.getValue() as string | null) || '—', size: 70 },
    { accessorKey: 'customerName', header: 'Customer', cell: (i) => (i.getValue() as string | null) || '—', size: 200 },
    { accessorKey: 'billtoName', header: 'Bill-To', cell: (i) => (i.getValue() as string | null) || '—', size: 200 },
    { accessorKey: 'migratedAt', header: 'Migrated On', cell: (i) => fmtDateTime(i.getValue() as string | null), size: 160 },
    {
      accessorKey: 'verified',
      header: 'Verified',
      cell: (i) => {
        const v = i.getValue() as boolean;
        return (
          <span className={`inline-flex px-2 py-0.5 rounded-full text-[0.6875rem] font-medium ${v
            ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
            : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'}`}>
            {v ? 'Verified' : 'Unverified'}
          </span>
        );
      },
      size: 110,
    },
    { accessorKey: 'verifiedBy', header: 'Verified By', cell: (i) => (i.getValue() as string | null) || '—', size: 130 },
    { accessorKey: 'verifiedAt', header: 'Verified On', cell: (i) => fmtDateTime(i.getValue() as string | null), size: 160 },
  ], []);

  if (error) {
    return <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">{error}</div>;
  }

  return (
    <div className="space-y-3">
      <p className="text-[0.78125rem] text-slate-500 dark:text-slate-400">
        Every quote migrated by this tool, newest first — the v1/v2 quote, when it was migrated, and verify status.
        (“Migrated On” is the migration timestamp; who ran the migration isn’t recorded.)
      </p>
      <TanTable
        data={rows}
        columns={columns}
        loading={loading}
        cardTitle="Migration History"
        searchPlaceholder={`Search ${rows.length} migrated quotes…`}
        settingsKey="quote-migration-history"
        enableSearch
        enableReset
        enableShowHideCols
        enableColumnFilters
        defaultSorting={[{ id: 'migratedAt', desc: true }]}
      />
    </div>
  );
};

/** "Rate Zone Mapping" tab — map each distinct v2 rate zone to a v1 cms.ratezones
 *  value. Sync scans the active set (auto-prefilling exact matches); the admin maps
 *  the rest. The migration translates a lane's PickupZone through this table;
 *  unmapped zones migrate with a blank rate zone + a review flag. */
const RateZonesView: React.FC = () => {
  const [rows, setRows] = useState<RateZoneMappingRow[]>([]);
  const [catalog, setCatalog] = useState<string[]>([]);
  const { wq, wqReady, wqError } = useWorklistQuotes();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingZone, setSavingZone] = useState<string | null>(null);
  const [picks, setPicks] = useState<Record<string, string>>({});   // v2_zone → selected v1 zone
  const [onlyUnmapped, setOnlyUnmapped] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState<string | null>(null);

  const applyRows = (r: RateZoneMappingRow[]) => {
    setRows(r);
    setPicks(Object.fromEntries(r.map((row) => [row.v2_zone, row.v1_ratezone ?? ''])));
  };
  const reload = async () => applyRows(await quoteMigrationService.listRatezones());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([quoteMigrationService.ratezoneCatalog(), quoteMigrationService.listRatezones()])
      .then(([cat, r]) => { if (cancelled) return; setCatalog(cat); applyRows(r); })
      .catch((e) => !cancelled && setError(errMsg(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const sync = async () => {
    setSyncing(true); setError(null); setSyncMsg(null);
    try {
      const res = await quoteMigrationService.syncRatezones();
      setSyncMsg(`Synced ${res.synced} v2 zones — ${res.prefilled} auto-mapped, ${res.unmapped} need a v1 zone.`);
      await reload();
    } catch (e) { setError(errMsg(e)); }
    finally { setSyncing(false); }
  };

  const save = async (zone: string, v1: string | null) => {
    setSavingZone(zone); setError(null);
    try {
      await quoteMigrationService.updateRatezone(zone, v1);
      await reload();
    } catch (e) { setError(errMsg(e)); }
    finally { setSavingZone(null); }
  };

  const isMapped = (row: RateZoneMappingRow) => !!(row.v1_ratezone && row.v1_ratezone.trim());
  const visible = useMemo(() => (onlyUnmapped ? rows.filter((r) => !isMapped(r)) : rows), [rows, onlyUnmapped]);
  const unmappedCount = useMemo(() => rows.filter((r) => !isMapped(r)).length, [rows]);

  if (loading) return <div className="text-slate-500">Loading…</div>;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <button type="button" onClick={sync} disabled={syncing}
          className="px-3 py-1.5 text-[0.8125rem] font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">
          {syncing ? 'Syncing…' : 'Sync Rate Zones'}
        </button>
        <span className="text-[0.78125rem] text-slate-500 dark:text-slate-400">{rows.length} zones · {unmappedCount} unmapped</span>
        {syncMsg && <span className="text-[0.78125rem] text-green-700 dark:text-green-400">{syncMsg}</span>}
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">{error}</div>}

      <label className="flex items-center gap-2 text-[0.78125rem] text-slate-600 dark:text-slate-300 cursor-pointer select-none">
        <input type="checkbox" checked={onlyUnmapped} onChange={(e) => setOnlyUnmapped(e.target.checked)} />
        Show only unmapped zones
      </label>

      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-[0.78125rem]">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-left text-slate-500 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2">v2 Rate Zone</th>
              <th className="px-3 py-2">Used</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Map to v1 rate zone</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">
                {rows.length === 0 ? 'No zones yet — click Sync Rate Zones.' : 'All zones mapped. 🎉'}
              </td></tr>
            )}
            {visible.map((row) => {
              const mapped = isMapped(row);
              return (
                <tr key={row.v2_zone} className="border-t border-slate-100 dark:border-slate-800 text-slate-800 dark:text-slate-200">
                  <td className="px-3 py-2 font-medium">{row.v2_zone}</td>
                  <td className="px-3 py-2">
                    <UsedCell count={row.occurrence_count} quotes={wq?.ratezones[row.v2_zone]}
                      ready={wqReady} error={wqError} kind="zone" />
                  </td>
                  <td className="px-3 py-2">
                    {mapped
                      ? <span className="px-2 py-0.5 rounded text-[0.6875rem] bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">{row.auto_prefilled ? 'auto' : 'mapped'}</span>
                      : <span className="px-2 py-0.5 rounded text-[0.6875rem] bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">unmapped</span>}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      className="w-full max-w-md px-2 py-1 border border-slate-300 dark:border-slate-600 rounded dark:bg-slate-700"
                      value={picks[row.v2_zone] ?? ''}
                      onChange={(e) => setPicks((p) => ({ ...p, [row.v2_zone]: e.target.value }))}
                    >
                      <option value="">— unmapped (leave blank) —</option>
                      {catalog.map((z) => <option key={z} value={z}>{z}</option>)}
                    </select>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button type="button" disabled={savingZone === row.v2_zone}
                      onClick={() => save(row.v2_zone, picks[row.v2_zone] ? picks[row.v2_zone] : null)}
                      className="px-3 py-1 text-[0.75rem] font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50">
                      {savingZone === row.v2_zone ? 'Saving…' : 'Save'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

/**
 * Admin "Quote Migration" page (v2→v1). Phase 1: resolve the charge cross-walk —
 * approve which v1 additionalcharges each v2 charge maps to (or skip it). The
 * worklist is synced by scripts/v2_quote_migration/migrate.py --sync-worklist.
 */
const STATUS_STYLES: Record<string, string> = {
  matched: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  ambiguous: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  unmatched: 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300',
};

const QuoteMigration: React.FC = () => {
  const [rows, setRows] = useState<ChargeMappingRow[]>([]);
  const { wq, wqReady, wqError } = useWorklistQuotes();
  const [catalog, setCatalog] = useState<CatalogRow[]>([]);
  const [stats, setStats] = useState<MigrationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingRecno, setSavingRecno] = useState<number | null>(null);
  const [picks, setPicks] = useState<Record<number, string>>({});   // row.recno → selected catalog recno (string)
  const [onlyReview, setOnlyReview] = useState(true);
  const [tab, setTab] = useState<'charges' | 'ratezones' | 'quotes' | 'verify' | 'history'>('charges');
  useFavoriteTab(tab, setTab, ['charges', 'ratezones', 'quotes', 'verify', 'history']);

  const reload = async () => {
    const [r, s] = await Promise.all([quoteMigrationService.listCharges(), quoteMigrationService.stats()]);
    setRows(r);
    setStats(s);
    setPicks(Object.fromEntries(r.map((row) => [
      row.recno, String(row.additionalcharges_recno ?? row.suggested_recno ?? ''),
    ])));
  };

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([quoteMigrationService.catalog(), quoteMigrationService.listCharges(), quoteMigrationService.stats()])
      .then(([cat, r, s]) => {
        if (cancelled) return;
        setCatalog(cat);
        setRows(r);
        setStats(s);
        setPicks(Object.fromEntries(r.map((row) => [
          row.recno, String(row.additionalcharges_recno ?? row.suggested_recno ?? ''),
        ])));
      })
      .catch((e) => !cancelled && setError(e instanceof Error ? e.message : String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const save = async (row: ChargeMappingRow, recno: number | null) => {
    setSavingRecno(row.recno);
    setError(null);
    try {
      await quoteMigrationService.updateCharge(row.recno, recno, true);
      await reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSavingRecno(null);
    }
  };

  const catalogOptions = useMemo(
    () => catalog.map((c) => ({ value: String(c.recno), label: `${c.description} (#${c.recno})` })),
    [catalog],
  );

  const visible = useMemo(
    () => (onlyReview ? rows.filter((r) => !r.approved && r.auto_match_status !== 'matched') : rows),
    [rows, onlyReview],
  );

  if (loading) return <div className="p-6 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-slate-800 dark:text-slate-100">Quote Migration</h1>
        <p className="text-[0.8125rem] text-slate-500 dark:text-slate-400">
          Resolve the v2→v1 charge mappings, then preview the proposed quotes. Migration writes use only approved rows.
        </p>
      </div>

      <div className="flex gap-1 border-b border-slate-200 dark:border-slate-700">
        {([['charges', 'Charge Mapping'], ['ratezones', 'Rate Zone Mapping'], ['quotes', 'Quotes Not Yet Migrated'], ['verify', 'Migrated · Unverified'], ['history', 'Migration History']] as const).map(([id, label]) => (
          <button key={id} type="button" onClick={() => setTab(id)}
            className={`px-3 py-1.5 text-[0.8125rem] -mb-px border-b-2 ${tab === id ? 'border-blue-600 text-blue-700 dark:text-blue-400 font-medium' : 'border-transparent text-slate-500 hover:text-slate-700'}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'ratezones' && <RateZonesView />}

      {tab === 'quotes' && <QuotesView />}

      {tab === 'verify' && <MigratedUnverifiedView />}

      {tab === 'history' && <MigrationHistoryView />}

      {tab === 'charges' && stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[
            ['Total charges', stats.total, ''],
            ['Auto-matched', stats.auto_matched, 'text-green-700 dark:text-green-400'],
            ['Needs review', stats.needs_review, 'text-red-700 dark:text-red-400'],
            ['Approved', stats.approved, 'text-blue-700 dark:text-blue-400'],
          ].map(([label, val, cls]) => (
            <div key={label as string} className="rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 py-3">
              <div className="text-[0.6875rem] uppercase tracking-wide text-slate-500 dark:text-slate-400">{label}</div>
              <div className={`text-2xl font-semibold ${cls as string}`}>{val as number}</div>
            </div>
          ))}
        </div>
      )}

      {tab === 'charges' && error && (
        <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3 text-sm text-red-800 dark:text-red-300">{error}</div>
      )}

      {tab === 'charges' && (
      <label className="flex items-center gap-2 text-[0.78125rem] text-slate-600 dark:text-slate-300 cursor-pointer select-none">
        <input type="checkbox" checked={onlyReview} onChange={(e) => setOnlyReview(e.target.checked)} />
        Show only charges needing review
      </label>
      )}

      {tab === 'charges' && (
      <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-700">
        <table className="w-full text-[0.78125rem]">
          <thead className="bg-slate-50 dark:bg-slate-800/60 text-left text-slate-500 dark:text-slate-400">
            <tr>
              <th className="px-3 py-2">v2 Charge</th>
              <th className="px-3 py-2">Used</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">Map to v1 charge</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 && (
              <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400">Nothing to review — all charges resolved.</td></tr>
            )}
            {visible.map((row) => (
              <tr key={row.recno} className="border-t border-slate-100 dark:border-slate-800 text-slate-800 dark:text-slate-200">
                <td className="px-3 py-2">
                  <div className="font-medium">{row.v2_charge_code}</div>
                  {row.v2_description && row.v2_description !== row.v2_charge_code && (
                    <div className="text-[0.6875rem] text-slate-500">{row.v2_description}</div>
                  )}
                </td>
                <td className="px-3 py-2">
                  <UsedCell count={row.occurrence_count} quotes={wq?.charges[row.v2_charge_code]}
                    ready={wqReady} error={wqError} kind="charge" />
                </td>
                <td className="px-3 py-2">
                  {row.approved
                    ? <span className="px-2 py-0.5 rounded text-[0.6875rem] bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">approved</span>
                    : <span className={`px-2 py-0.5 rounded text-[0.6875rem] ${STATUS_STYLES[row.auto_match_status ?? ''] ?? ''}`}>{row.auto_match_status}</span>}
                </td>
                <td className="px-3 py-2">
                  <select
                    className="w-full max-w-md px-2 py-1 border border-slate-300 dark:border-slate-600 rounded dark:bg-slate-700"
                    value={picks[row.recno] ?? ''}
                    onChange={(e) => setPicks((p) => ({ ...p, [row.recno]: e.target.value }))}
                  >
                    <option value="">— skip (no v1 charge) —</option>
                    {catalogOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button
                    type="button"
                    disabled={savingRecno === row.recno}
                    onClick={() => save(row, picks[row.recno] ? Number(picks[row.recno]) : null)}
                    className="px-3 py-1 text-[0.75rem] font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {savingRecno === row.recno ? 'Saving…' : (picks[row.recno] ? 'Approve' : 'Skip')}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
};

export default QuoteMigration;
